// Clinic / Internal compliance service.
//
// Powers the "Clinic" tab of the Compliance page: access-activity logging,
// SOC2 / HIPAA controls tracking, and the orders lifecycle (generated →
// assigned → executed).
//
// NOTE: Access-log and controls remain sample-backed until those backend
// endpoints exist. Orders/followups use the real clinic endpoints.
//
// Who is asking (backend/api/clinic_orders.py): every orders call sends the
// signed-in user's token, with one refresh-and-retry after a 401
// (staffRequest), and the server takes the assigner, the actor and the one who
// completed an order from it. No body names the signed-in user: the options
// that did (assignedBy, actorId, completedBy, updatedBy) are kept so that
// callers need not change, and are never sent.

import { getBackendURL } from "../components/env";
import { supabaseSchema } from "../components/supabaseClient";
import { staffRequest } from "./staffRequest";

function activeSchema(override) {
  if (override) return override;
  const raw = (supabaseSchema || "public").trim();
  return raw.includes(",") ? raw.split(",")[0].trim() : raw;
}

function clinicFetch(path, { schema, signal, params, failure = "Clinic compliance API error" } = {}) {
  const base = getBackendURL();
  const url = new URL(`${base}/api/compliance/clinic${path}`);
  url.searchParams.set("schema", activeSchema(schema));
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value != null && value !== "") url.searchParams.set(key, String(value));
  });
  return staffRequest(url.toString(), { signal, failure });
}

// ---------------------------------------------------------------------------
// Access activity log — who accessed which page (SOC2 CC7 / HIPAA §164.312(b))
// ---------------------------------------------------------------------------
export async function fetchAccessLog(opts = {}) {
  void opts;
  return { ...SAMPLE_ACCESS_LOG, _sample: true };
}

// ---------------------------------------------------------------------------
// Controls tracking — SOC2 / HIPAA control posture
// ---------------------------------------------------------------------------
export async function fetchControls(opts = {}) {
  void opts;
  return { ...SAMPLE_CONTROLS, _sample: true };
}

// ---------------------------------------------------------------------------
// Orders & Followups — audio-derived clinical action items
// ---------------------------------------------------------------------------
const EMPTY_ORDERS = {
  summary: { unassigned: 0, pending: 0, completed: 0, total: 0 },
  patients: [],
};

// `skipDateRepair` sends repair=0: the backend then skips its visit-date
// repair, which can walk storage on every call. For callers that only need
// statuses (the Schedules row actions), not for the chart's Orders modal.
export async function fetchOrders(opts = {}) {
  const { skipDateRepair, ...rest } = opts;
  try {
    return await clinicFetch("/orders/", {
      ...rest,
      ...(skipDateRepair ? { params: { repair: "0" } } : {}),
    });
  } catch (err) {
    console.warn("Clinic orders: backend unavailable.", err);
    return { ...EMPTY_ORDERS, _error: true };
  }
}

export async function fetchOrdersForAudio(ehrId, audioNumber, opts = {}) {
  return clinicFetch("/orders/", {
    schema: opts.schema,
    signal: opts.signal,
    params: { ehr_id: ehrId, audio_number: audioNumber },
    failure: "Clinic orders API error",
  });
}

export async function searchClinicPatients(query, opts = {}) {
  if (!query || query.trim().length < 1) return { patients: [], count: 0 };
  return clinicFetch("/patients/search/", {
    schema: opts.schema,
    signal: opts.signal,
    params: { q: query.trim() },
    failure: "Clinic patient search API error",
  });
}

// The staff of the signed-in user's own clinics whose name or email holds
// `query` (backend/api/ai_assistant.py search_users, Plan 4 Task 11): with the
// token, as every call here; the server takes the clinics from it.
export async function searchUsers(query, opts = {}) {
  if (!query || query.trim().length < 1) return { users: [], count: 0 };
  return staffRequest(`${getBackendURL()}/api/users/search/`, {
    method: "POST",
    body: { query: query.trim() },
    signal: opts.signal,
    failure: "User search API error",
  });
}

// The ids a body could name the signed-in user by. The server takes them from
// the token, so none is ever sent, whoever put one in a payload.
const SELF_IDS = ["assigned_by_user_id", "actor_user_id", "completed_by_user_id", "updated_by_user_id"];

function clinicPost(path, body, { schema } = {}) {
  const sent = { ...body, schema: activeSchema(schema) };
  SELF_IDS.forEach((key) => delete sent[key]);
  return staffRequest(`${getBackendURL()}/api/compliance/clinic${path}`, {
    method: "POST",
    body: sent,
    failure: "Clinic orders API error",
  });
}

// opts.priority (high | medium | low) sets the order's priority as it's assigned.
export const assignOrderItem = (itemId, userId, opts = {}) =>
  clinicPost("/orders/assign/", {
    item_id: itemId, user_id: userId,
    ...(opts.priority ? { priority: opts.priority } : {}),
  }, opts);

export const unassignOrderItem = (itemId, opts = {}) =>
  clinicPost("/orders/unassign/", {
    item_id: itemId,
    ...(opts.message ? { message: opts.message } : {}),
  }, opts);

export const completeOrderItem = (itemId, completed, opts = {}) =>
  clinicPost("/orders/complete/", { item_id: itemId, completed }, opts);

export const updateOrderItemStatus = (itemId, status, opts = {}) =>
  clinicPost("/orders/status/", { item_id: itemId, status }, opts);

// Review-aware status change (see backend/api/task_review.py), taken as the
// signed-in user. `message` (reject) goes into the order's activity feed.
export const transitionOrderItem = (itemId, action, opts = {}) =>
  clinicPost("/orders/status/", {
    item_id: itemId, action,
    ...(opts.message ? { message: opts.message } : {}),
    ...(opts.to ? { to: opts.to } : {}),
  }, opts);

export const updateOrderItem = (itemId, payload, opts = {}) =>
  clinicPost("/orders/update/", { item_id: itemId, ...payload }, opts);

export const createOrderItem = (payload, opts = {}) =>
  clinicPost("/orders/create/", payload, opts);

export const syncOrders = (opts = {}) => clinicPost("/orders/sync/", {}, opts);

// ===========================================================================
// Sample data (placeholder until backend endpoints exist)
// ===========================================================================

const SAMPLE_ACCESS_LOG = {
  summary: { events_24h: 184, unique_users: 11, failed_auth: 2, exports: 3 },
  events: [
    { id: "a1", at: "2026-06-12T14:58:03Z", user: "dr.leejoe@vitamedai.com", role: "physician", action: "view", resource: "Patient Chart · #8821", ip: "73.12.44.9", status: "ok" },
    { id: "a2", at: "2026-06-12T14:51:40Z", user: "nurse.kim@vitamedai.com", role: "nurse", action: "edit", resource: "SOAP Note · visit 5567", ip: "73.12.44.21", status: "ok" },
    { id: "a3", at: "2026-06-12T14:45:11Z", user: "janu@vitamedai.com", role: "admin", action: "export", resource: "Compliance CSV (payer)", ip: "98.4.10.2", status: "ok" },
    { id: "a4", at: "2026-06-12T14:39:55Z", user: "unknown", role: "—", action: "login", resource: "Auth endpoint", ip: "201.55.8.140", status: "failed" },
    { id: "a5", at: "2026-06-12T14:30:02Z", user: "front.desk@vitamedai.com", role: "staff", action: "view", resource: "Appointments · 2026-06-12", ip: "73.12.44.30", status: "ok" },
    { id: "a6", at: "2026-06-12T14:22:48Z", user: "dr.leejoe@vitamedai.com", role: "physician", action: "view", resource: "Compliance · Patients tab", ip: "73.12.44.9", status: "ok" },
    { id: "a7", at: "2026-06-12T14:10:17Z", user: "billing@vitamedai.com", role: "staff", action: "view", resource: "Orders Dashboard", ip: "73.12.44.55", status: "ok" },
    { id: "a8", at: "2026-06-12T13:58:30Z", user: "janu@vitamedai.com", role: "admin", action: "settings", resource: "Compliance Admin Settings", ip: "98.4.10.2", status: "ok" },
  ],
};

const SAMPLE_CONTROLS = {
  summary: { total: 6, passing: 4, attention: 1, failing: 1 },
  controls: [
    { id: "CC6.1", framework: "SOC2", name: "Logical access — MFA enforced", owner: "Janu S.", status: "passing", last_reviewed: "2026-06-01", evidence: "Supabase Auth MFA policy" },
    { id: "CC6.7", framework: "SOC2", name: "Data in transit encrypted (TLS 1.2+)", owner: "Janu S.", status: "passing", last_reviewed: "2026-05-28", evidence: "Render TLS config" },
    { id: "CC7.2", framework: "SOC2", name: "Access activity monitored & logged", owner: "Janu S.", status: "attention", last_reviewed: "2026-05-15", evidence: "Audit log pipeline pending" },
    { id: "§164.312(a)(1)", framework: "HIPAA", name: "Access control — unique user IDs + RBAC", owner: "Leejoe", status: "passing", last_reviewed: "2026-06-02", evidence: "Role config + RLS" },
    { id: "§164.312(b)", framework: "HIPAA", name: "Audit controls — PHI access recorded", owner: "Janu S.", status: "failing", last_reviewed: "2026-04-30", evidence: "Not yet implemented" },
    { id: "§164.308(a)(1)", framework: "HIPAA", name: "Risk analysis reviewed periodically", owner: "Leejoe", status: "passing", last_reviewed: "2026-05-20", evidence: "Q2 risk assessment" },
  ],
};
